import PropTypes from 'prop-types';
import React from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  TextField,
  Divider,
  CardContent,
  Typography,
} from '@material-ui/core';

// General Settings — trimmed to the only field this page actually persists.
//
// Background (see docs/SETTINGS_AUDIT.md, May-2026 second pass):
// The BO previously exposed commission_perc / extra_cost / max_req_radius /
// cancel_time / cancel_fee / cancel_price / cancel_commission here. Those
// values are read at runtime, BUT:
//   • PATCH /settings (this page's endpoint) never persisted them — only
//     PATCH /service_settings does (Services Settings page). So edits here
//     were silently dropped.
//   • cancel_fee is dead code at runtime — only cancel_time_seconds is used
//     by the cancellation logic; cancel_fee is seeded into service rows but
//     never actually read by any business rule.
//   • cancel_commission was removed entirely on 2026-05-20 — washers earn
//     no commission on canceled orders now.
// Showing them here was confusing and gave the false impression they had
// been saved — edit them on Services Settings instead.
//
// Only `company_email` is genuinely owned by this page. It is the ADMIN
// NOTIFICATION RECIPIENT (e.g. who gets the "novo washer" email when a new
// washer signs up). It is NOT the From: address — the From: is configured
// via env (MAIL_FROM_EMAIL, default: call@washer.pt).
// If a future field comes back, also extend SETTINGS_UPDATABLE_COLUMNS in
// washer-api/functions/services/settings.js so it actually persists.
const GeneralSettings = ({ handleGeneralSetting, settings }) => {
  const s = settings || {};

  return (
    <Card>
      <CardHeader
        title="General Settings"
        subheader="Admin notification recipient — receives system emails like 'novo washer'. (Pricing, commission, cancellation and operation radius are managed under Services → Settings.)"
      />
      <Divider />
      <CardContent>
        <Formik
          enableReinitialize
          initialValues={{
            company_email: s.company_email ?? '',
          }}
          onSubmit={(values) => {
            handleGeneralSetting({
              company_email: values.company_email,
            });
          }}
        >
          {({ handleChange, handleSubmit, isSubmitting, values }) => (
            <form onSubmit={handleSubmit}>
              <Box pb={2}>
                <Typography variant="caption" color="textSecondary">
                  Pricing, commission, cancellation, and max operation radius
                  live under <strong>Services → Settings</strong>. Terms &
                  Conditions and Privacy have their own pages. This form only
                  controls the email address that receives admin notifications
                  (e.g. when a new washer signs up).
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Admin notification email"
                    name="company_email"
                    onChange={handleChange}
                    value={values.company_email}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Box paddingY={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      size="large"
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      UPDATE
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

GeneralSettings.propTypes = {
  handleGeneralSetting: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
};

export default GeneralSettings;
